import { createContext, useContext, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { PostHogProvider, usePostHog } from '@posthog/react';
import type { PostHog } from 'posthog-js';
import type { PostHogInterface } from 'posthog-js';
import { useAuth } from './contexts/AuthContext';

const isProd = import.meta.env.PROD;

// ── Analytics type ────────────────────────────────────────────────────────────
// `opt_out_capturing` is added on top of the minimal capture/identify set
// because the COPPA opt-out path needs it. PostHog persists the opt-out in
// its own state across reloads, so we only call it once per parent uid.

// Minimal subset of PostHog methods used in the app.
// Add more as needed (e.g. identify, group, setPersonProperties).
export type Analytics = Pick<PostHog, 'capture' | 'identify' | 'reset' | 'alias' | 'group' | 'opt_out_capturing'>;

const noopAnalytics: Analytics = {
  capture: () => undefined,
  identify: () => {},
  reset: () => {},
  alias: () => {},
  group: () => {},
  opt_out_capturing: () => {},
};

const AnalyticsContext = createContext<Analytics>(noopAnalytics);

// Inner bridge — only mounted inside <PostHogProvider> so usePostHog() is safe here.
function PostHogBridge({ children }: { children: ReactNode }) {
  const posthog = usePostHog();
  return (
    <AnalyticsContext.Provider value={posthog ?? noopAnalytics}>
      {children}
    </AnalyticsContext.Provider>
  );
}

const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  // Tag all events with the deployment environment so staging and production
  // data are distinguishable in the PostHog UI.
  loaded: (posthog: PostHogInterface) => {
    posthog.register({ environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'production' });
  },
};

/**
 * Wrap the app with this provider.
 * In production: mounts PostHogProvider + exposes the live PostHog client.
 * In development: skips PostHog entirely and exposes a no-op client.
 */
export function AnalyticsProvider({ children }: { children: ReactNode }) {
  if (!isProd) {
    return (
      <AnalyticsContext.Provider value={noopAnalytics}>
        {children}
      </AnalyticsContext.Provider>
    );
  }

  return (
    <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={posthogOptions}>
      <PostHogBridge>{children}</PostHogBridge>
    </PostHogProvider>
  );
}

/**
 * Use this instead of usePostHog() directly.
 * Returns the live PostHog client in production, or a no-op object in development.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAnalytics(): Analytics {
  return useContext(AnalyticsContext);
}

/**
 * Auth-aware analytics sync — mount this INSIDE AuthProvider (separate from
 * AnalyticsProvider which sits above AuthProvider in main.tsx).
 *
 * COPPA defensive: when the signed-in user is a parent-type account (the
 * "player" is a youth profile under 13), call analytics.opt_out_capturing()
 * for that session. PostHog persists the opt-out in its own client state
 * across reloads, so we never re-enable it for this user — even if they sign
 * out and back in. This complements the broader "no third-party data sharing
 * for under-13" policy in Privacy Policy §11.
 *
 * Idempotent per uid (only fires once per parent uid via a ref guard).
 */
export function AnalyticsAuthSync() {
  const analytics = useAnalytics();
  const { userProfile } = useAuth();
  const optedOutForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (userProfile?.accountType === 'parent' && userProfile.uid) {
      if (optedOutForUserRef.current !== userProfile.uid) {
        try {
          analytics.opt_out_capturing();
          optedOutForUserRef.current = userProfile.uid;
        } catch (err) {
          // Non-fatal — opt-out is a defensive layer; missing it doesn't
          // break the app and the policy still applies on the backend.
          console.warn('[analytics] opt_out_capturing failed', err);
        }
      }
    }
  }, [analytics, userProfile?.accountType, userProfile?.uid]);

  return null;
}
